import React, {useMemo} from 'react';
import {withGlobals, useGlobals} from '../../../globalPropsContext';
import {IGalleryGlobalProps} from '../../../gallery/galleryGlobalStrategy';
import {LazyRichContentViewer} from '../RichContentViewer/LazyRichContentViewer';
import {useStyles} from '@wix/tpa-settings/react';
import {useStylesParams} from '../../../stylesParamsContext';
import {TextButton} from 'wix-ui-tpa/cssVars';
import s from './FooterDescription.scss';
import {classes as stylableClasses} from './FooterDescription.st.css';
import {useExpandableRichContent} from './useExpandableRichContent';
import {CategoryFooterDescriptionDataHook} from './FooterDescription.constants';

export type FooterDescriptionProps = IGalleryGlobalProps;

export const FooterDescription: React.FC<FooterDescriptionProps> = withGlobals(() => {
  const {textsMap, currentCategory, isSEO} = useGlobals();
  const styles = useStyles();
  const stylesParams = useStylesParams();

  const contentAsString = currentCategory?.richContentDescription;

  if (!contentAsString) {
    return null;
  }

  const trimEnabled = styles.get(stylesParams.gallery_categoryFooterDescriptionTrimContent);
  const trimMode = styles.get(stylesParams.gallery_categoryFooterDescriptionTrimMode);
  const elementLimit = styles.get(stylesParams.gallery_categoryFooterDescriptionParagraphLimit);
  const wordLimit = styles.get(stylesParams.gallery_categoryFooterDescriptionWordLimit);

  const shouldTruncateForUser = trimEnabled && !isSEO;

  const {content, isExpanded, canBeTruncated, toggleExpansion} = useExpandableRichContent({
    contentAsString,
    trimEnabled: shouldTruncateForUser,
    trimMode,
    elementLimit,
    wordLimit,
  });

  const ricoStylesParams = useMemo(
    () => ({
      paragraphColor: styles.get(stylesParams.gallery_categoryFooterDescriptionParagraphColor),
      paragraphFont: styles.get(stylesParams.gallery_categoryFooterDescriptionParagraphFont),
      alignment: styles.get(stylesParams.gallery_categoryFooterDescriptionAlignment),
      heading1Font: styles.get(stylesParams.gallery_categoryFooterDescriptionHeading1Font),
      heading1Color: styles.get(stylesParams.gallery_categoryFooterDescriptionHeading1Color),
      heading2Font: styles.get(stylesParams.gallery_categoryFooterDescriptionHeading2Font),
      heading2Color: styles.get(stylesParams.gallery_categoryFooterDescriptionHeading2Color),
      heading3Font: styles.get(stylesParams.gallery_categoryFooterDescriptionHeading3Font),
      heading3Color: styles.get(stylesParams.gallery_categoryFooterDescriptionHeading3Color),
      heading4Font: styles.get(stylesParams.gallery_categoryFooterDescriptionHeading4Font),
      heading4Color: styles.get(stylesParams.gallery_categoryFooterDescriptionHeading4Color),
      heading5Font: styles.get(stylesParams.gallery_categoryFooterDescriptionHeading5Font),
      heading5Color: styles.get(stylesParams.gallery_categoryFooterDescriptionHeading5Color),
      heading6Font: styles.get(stylesParams.gallery_categoryFooterDescriptionHeading6Font),
      heading6Color: styles.get(stylesParams.gallery_categoryFooterDescriptionHeading6Color),
      quoteFont: styles.get(stylesParams.gallery_categoryFooterDescriptionQuoteFont),
      quoteColor: styles.get(stylesParams.gallery_categoryFooterDescriptionQuoteColor),
    }),
    [styles, stylesParams]
  );

  const renderReadMoreButton = () => {
    if (!shouldTruncateForUser || !canBeTruncated) {
      return null;
    }

    return (
      <TextButton
        className={stylableClasses.readMoreButton}
        onClick={toggleExpansion}
        data-hook={CategoryFooterDescriptionDataHook.ReadMoreButton}>
        {!isExpanded ? textsMap.categoryFooterDescriptionShowMore : textsMap.categoryFooterDescriptionShowLess}
      </TextButton>
    );
  };

  return (
    <div className={s.root} data-hook={CategoryFooterDescriptionDataHook.Root}>
      <div
        className={s.contentContainer}
        data-hook={CategoryFooterDescriptionDataHook.ContentContainer}
        style={{textAlign: styles.get(stylesParams.gallery_categoryFooterDescriptionAlignment).value as any}}>
        <LazyRichContentViewer
          content={content}
          ricosContext="category-footer-description"
          dataHook={CategoryFooterDescriptionDataHook.Viewer}
          stylesParams={ricoStylesParams}
        />
        {renderReadMoreButton()}
      </div>
    </div>
  );
});
